import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { apiClient } from '../services/api';

/** GET /ai/status — 서버 공용 grk-ai */
interface CliStatus { configured: boolean; connected: boolean; running?: number; queued?: number; max?: number; paused?: boolean; error?: string }

/** GET /ai/usage — 이 앱 전체 합계(사람 · 키 · 모델로 나누지 않는다) */
interface Usage {
  from: string;
  to: string;
  totals: { calls: number; failed: number; inputTokens: number; outputTokens: number; avgMs: number | null };
  daily: { day: string; calls: number }[];
  byPurpose: { purpose: string; calls: number }[];
  failures: { id: number; at: string; purpose: string; error: string | null }[];
}

/** 기능 이름 — consult_ai_calls.purpose */
const PURPOSE_LABEL: Record<string, string> = {
  'bid.summary': '공고 요약',
  'rfp.eval': '배점표 추출',
  'proposal.draft': '제안서 초안',
  'intake.read': '접수서 읽기',
  'deck.draft': '문서 초안',
  'archive.extract': '아카이브 추출',
};
const purpose = (p: string) => PURPOSE_LABEL[p] ?? p;

type Period = 'month' | 'prev' | '7' | '30' | '90';
const PERIODS: { key: Period; label: string }[] = [
  { key: 'month', label: '이번 달' },
  { key: 'prev', label: '지난 달' },
  { key: '7', label: '최근 7일' },
  { key: '30', label: '최근 30일' },
  { key: '90', label: '최근 90일' },
];

const p2 = (x: number) => String(x).padStart(2, '0');
const ymd = (d: Date) => `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;

function range(k: Period): { from: string; to: string } {
  const t = new Date();
  if (k === 'month') return { from: ymd(new Date(t.getFullYear(), t.getMonth(), 1)), to: ymd(t) };
  if (k === 'prev') return { from: ymd(new Date(t.getFullYear(), t.getMonth() - 1, 1)), to: ymd(new Date(t.getFullYear(), t.getMonth(), 0)) };
  return { from: ymd(new Date(t.getFullYear(), t.getMonth(), t.getDate() - Number(k) + 1)), to: ymd(t) };
}

/** from ~ to 의 날마다 — 호출이 없는 날도 막대 자리를 둔다 */
function days(from: string, to: string): string[] {
  const out: string[] = [];
  const [y, m, d] = from.split('-').map(Number);
  for (let c = new Date(y, m - 1, d); ymd(c) <= to; c.setDate(c.getDate() + 1)) out.push(ymd(c));
  return out;
}

const num = (n: number | null | undefined) => (n == null ? '—' : n.toLocaleString('ko-KR'));
const compact = (n: number) => (n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${Math.round(n / 1e3)}K` : String(n));
const when = (d: Date) => `${d.getMonth() + 1}/${d.getDate()} ${p2(d.getHours())}:${p2(d.getMinutes())}`;

type Light = 'ok' | 'pause' | 'err';
const LIGHT: Record<Light, { dot: string; ink: string; why: string; icon: string; label: string }> = {
  ok: { dot: 'bg-emerald-50 text-emerald-600', ink: 'text-emerald-600', why: '', icon: '✓', label: '정상' },
  pause: { dot: 'bg-amber-50 text-amber-600', ink: 'text-amber-600', why: 'bg-amber-50 text-amber-800', icon: 'Ⅱ', label: '일시정지' },
  err: { dot: 'bg-red-50 text-red-600', ink: 'text-red-600', why: 'bg-red-50 text-red-800', icon: '!', label: '오류' },
};

function light(s: CliStatus): { k: Light; detail: string; why: string } {
  if (!s.configured) return { k: 'err', detail: '이 서버에 AI 연결 설정이 없습니다', why: 'AI 연결 설정(키)이 없어 AI 기능을 쓸 수 없습니다. 관리자에게 문의하세요.' };
  if (!s.connected) return { k: 'err', detail: 'AI 서버에 연결할 수 없습니다', why: `AI 서버가 응답하지 않습니다${s.error ? ` (${s.error})` : ''}. 잠시 뒤 「다시 확인」을 눌러 보고, 계속되면 관리자에게 문의하세요.` };
  if (s.paused) return { k: 'pause', detail: '새 요청을 받지 않습니다', why: '관리자가 AI 를 잠시 멈췄습니다. AI 기능(공고 요약 · 추출 · 초안)은 다시 켜질 때까지 쓸 수 없습니다. 급하면 관리자에게 문의하세요.' };
  return { k: 'ok', detail: `처리 중 ${s.running ?? 0} / ${s.max ?? '?'} · 대기 ${s.queued ?? 0}`, why: '' };
}

/**
 * AI 관리 — 서버 공용 AI(grk-ai) 상태와 이 앱의 사용량(2026-10-27).
 * 「다시 확인」은 상태를 다시 읽기만 한다. 켜고 끄기 · 재연결은 grk-ai 관리자가 한다.
 */
export const AiPage: React.FC = () => {
  const [status, setStatus] = useState<CliStatus | null>(null);
  const [checkedAt, setCheckedAt] = useState<Date | null>(null);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [period, setPeriod] = useState<Period>('month');
  const [error, setError] = useState('');

  const loadStatus = useCallback(async () => {
    setStatus(null);
    try { setStatus(await apiClient.get('/ai/status')); } catch { setStatus({ configured: true, connected: false, error: '상태를 읽지 못했습니다' }); }
    setCheckedAt(new Date());
  }, []);
  const loadUsage = useCallback(async () => {
    const r = range(period);
    setUsage(null);
    try { setUsage(await apiClient.get(`/ai/usage?from=${r.from}&to=${r.to}`)); setError(''); } catch (e) { setError((e as Error).message); }
  }, [period]);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { loadUsage(); }, [loadUsage]);

  return (
    <div className="p-6 lg:p-8 max-w-5xl space-y-4">
      <div>
        <h1 className="text-xl font-bold text-slate-900">AI 관리</h1>
        <p className="mt-1 text-sm text-slate-500">서버 공용 AI(grk-ai) 상태와 이 앱의 사용량</p>
      </div>

      <StatusCard status={status} checkedAt={checkedAt} onRecheck={loadStatus} />

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</div>}

      <section className="bg-white rounded-lg border border-slate-200 px-5 py-4 space-y-4">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold text-slate-900">사용량</h2>
          <select value={period} onChange={(e) => setPeriod(e.target.value as Period)} className="ml-auto px-2 py-1 border border-slate-300 rounded-lg text-xs">
            {PERIODS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
        </div>
        {!usage ? (
          <p className="flex items-center gap-2 py-6 text-sm text-slate-400"><Loader2 size={14} className="animate-spin" /> 불러오는 중…</p>
        ) : (
          <UsageBody u={usage} />
        )}
      </section>

      <section className="bg-white rounded-lg border border-slate-200 px-5 py-4">
        <div className="flex items-center mb-2">
          <h2 className="text-sm font-semibold text-slate-900">최근 실패</h2>
          <span className="ml-auto text-xs text-slate-400">최근 10건</span>
        </div>
        {!usage ? null : usage.failures.length === 0 ? (
          <p className="py-4 text-sm text-slate-400">실패한 호출이 없습니다.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-200">
                  <th className="py-1.5 pr-3 font-medium">시각</th>
                  <th className="py-1.5 pr-3 font-medium">기능</th>
                  <th className="py-1.5 font-medium">원인</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {usage.failures.map((f) => (
                  <tr key={f.id} className="align-top">
                    <td className="py-2 pr-3 whitespace-nowrap tabular-nums text-slate-600">{when(new Date(f.at))}</td>
                    <td className="py-2 pr-3 whitespace-nowrap text-slate-700">{purpose(f.purpose)}</td>
                    <td className="py-2 text-red-600 break-all">{f.error ?? '(원인 기록 없음)'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

const StatusCard: React.FC<{ status: CliStatus | null; checkedAt: Date | null; onRecheck: () => void }> = ({ status, checkedAt, onRecheck }) => {
  const l = status ? light(status) : null;
  const st = l ? LIGHT[l.k] : null;
  return (
    <section className="bg-white rounded-lg border border-slate-200 px-5 py-4 space-y-3">
      <div className="flex items-center gap-2">
        <h2 className="text-sm font-semibold text-slate-900">AI 상태</h2>
        <span className="ml-auto text-xs text-slate-400 tabular-nums">{checkedAt && status ? `${when(checkedAt)} 확인` : ''}</span>
        <button onClick={onRecheck} disabled={!status} className="px-3 py-1 rounded-lg border border-indigo-200 text-xs font-semibold text-indigo-600 hover:bg-indigo-50 disabled:opacity-50">다시 확인</button>
      </div>
      {!l || !st ? (
        <p className="flex items-center gap-2 text-sm text-slate-400"><Loader2 size={14} className="animate-spin" /> 확인 중…</p>
      ) : (
        <>
          <div className="flex items-center gap-3.5">
            <div className={`w-11 h-11 rounded-full grid place-items-center text-xl font-bold ${st.dot}`}>{st.icon}</div>
            <div>
              <div className={`text-base font-semibold ${st.ink}`}>{st.label}</div>
              <div className="text-xs text-slate-500">{l.detail}</div>
            </div>
          </div>
          {l.why && <p className={`rounded-lg px-3 py-2.5 text-sm leading-relaxed ${st.why}`}>{l.why}</p>}
        </>
      )}
    </section>
  );
};

const UsageBody: React.FC<{ u: Usage }> = ({ u }) => {
  const t = u.totals;
  const list = useMemo(() => days(u.from, u.to), [u.from, u.to]);
  const per = useMemo(() => new Map(u.daily.map((d) => [d.day, d.calls])), [u.daily]);
  const max = Math.max(1, ...u.daily.map((d) => d.calls));
  const top = Math.max(1, ...u.byPurpose.map((p) => p.calls));
  const today = ymd(new Date());
  const W = 600, H = 70, base = 64, slot = W / list.length, bw = Math.max(2, slot * 0.7);

  if (t.calls === 0) return <p className="py-8 text-center text-sm text-slate-400">이 기간에 AI 를 부른 기록이 없습니다.</p>;
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
        <Kpi k="호출" v={num(t.calls)} unit="건" />
        <Kpi k="실패" v={num(t.failed)} unit="건" bad={t.failed > 0} />
        <Kpi k="토큰 입력 / 출력" v={compact(t.inputTokens)} unit={`/ ${compact(t.outputTokens)}`} />
        <Kpi k="평균 걸린 시간" v={t.avgMs == null ? '—' : String(Math.round(t.avgMs / 1000))} unit="초" />
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto block" role="img" aria-label="일별 호출 수">
        {list.map((d, i) => {
          const n = per.get(d) ?? 0;
          if (!n) return null;
          const h = Math.max(2, (n / max) * (base - 4));
          return (
            <rect key={d} x={i * slot + (slot - bw) / 2} y={base - h} width={bw} height={h} rx={2} className={d === today ? 'fill-indigo-600' : 'fill-indigo-300'}>
              <title>{`${d.slice(5).replace('-', '/')} · ${n}건`}</title>
            </rect>
          );
        })}
        <line x1={0} y1={base + 0.5} x2={W} y2={base + 0.5} className="stroke-slate-200" />
      </svg>

      <div>
        <h3 className="text-xs font-semibold text-slate-700 mb-2">기능별</h3>
        <div className="space-y-2">
          {u.byPurpose.map((p) => (
            <div key={p.purpose} className="grid grid-cols-[96px_minmax(0,1fr)_40px] gap-2 items-center text-xs text-slate-700">
              <span className="truncate">{purpose(p.purpose)}</span>
              <div className="h-2.5 rounded bg-slate-100 overflow-hidden"><i className="block h-full rounded bg-indigo-300" style={{ width: `${(p.calls / top) * 100}%` }} /></div>
              <span className="text-right tabular-nums">{num(p.calls)}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

const Kpi: React.FC<{ k: string; v: string; unit: string; bad?: boolean }> = ({ k, v, unit, bad }) => (
  <div className="rounded-lg bg-slate-50 px-3 py-2.5 min-w-0">
    <div className="text-[11px] text-slate-500">{k}</div>
    <div className={`text-xl font-semibold tabular-nums ${bad ? 'text-red-600' : 'text-slate-900'}`}>{v}<small className="ml-0.5 text-xs font-normal text-slate-500">{unit}</small></div>
  </div>
);
